import React, { useState, useEffect, useRef } from "react";
import Layout from "./Layout";
import DeviceLock from "../pages/DeviceLock";
import ForceSignInWarning from "../pages/ForceSignInWarning";
import Onboarding from "../pages/Onboarding";
import VerifyEmail from "../pages/VerifyEmail";
import NewsModal from "./NewsModal";
import { useUser } from "../context/UserProvider";
import { db } from "../lib/firebase";
import { collection, query, where, orderBy, getDocs, doc, updateDoc, Timestamp } from "firebase/firestore";

export default function ProtectedLayout() {
  const { deviceLock, claims, ready, needsProfile, needsEmailVerification, uid } = useUser();
  const privileged = claims?.admin || claims?.managerPlus;

  // News modal state
  const [newsModalVisible, setNewsModalVisible] = useState(false);
  const [newsItems, setNewsItems] = useState([]);
  const newsCheckedRef = useRef(false);

  // Fetch news on mount (after ready)
  useEffect(() => {
    if (!ready || !uid || newsCheckedRef.current) return;
    newsCheckedRef.current = true;

    (async () => {
      try {
        // Fetch user doc to get lastNewsReadAt
        const userSnap = await getDocs(query(collection(db, 'users'), where('__name__', '==', uid)));
        let lastNewsReadAt = new Date(0);
        if (!userSnap.empty) {
          const userData = userSnap.docs[0].data();
          lastNewsReadAt = userData.lastNewsReadAt?.toDate?.() || new Date(0);
        }

        const now = new Date();

        const newsQuery = query(
          collection(db, 'news'),
          where('active', '==', true),
          where('expiresAt', '>', Timestamp.fromDate(now)),
          orderBy('expiresAt', 'asc'),
          orderBy('createdAt', 'desc')
        );

        const snap = await getDocs(newsQuery);
        const items = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(item => {
            const createdAt = item.createdAt?.toDate?.() || new Date(0);
            if (createdAt <= lastNewsReadAt) return false;

            // Filter by target (app/web/both) - only show items for web or both
            const targets = item.targets || ['both'];
            const targetMatch = targets.includes('both') || targets.includes('web');
            if (!targetMatch) return false;

            return true;
          })
          .sort((a, b) => {
            const aPriority = a.priority || 0;
            const bPriority = b.priority || 0;
            if (bPriority !== aPriority) return bPriority - aPriority;
            const aDate = a.createdAt?.toDate?.() || new Date(0);
            const bDate = b.createdAt?.toDate?.() || new Date(0);
            return bDate.getTime() - aDate.getTime();
          });

        if (items.length > 0) {
          setNewsItems(items);
          setNewsModalVisible(true);
        }
      } catch (err) {
        console.warn('[news] Failed to fetch news:', err);
      }
    })();
  }, [ready, uid]);

  const handleCloseNews = async () => {
    setNewsModalVisible(false);
    if (uid) {
      try {
        await updateDoc(doc(db, 'users', uid), { lastNewsReadAt: Timestamp.now() });
      } catch (err) {
        console.warn('[news] Failed to mark news as read:', err);
      }
    }
  };

  // Avoid flashing Home before we know device lock status
  if (!ready || (!privileged && !deviceLock)) {
    return <div className="min-h-screen bg-naim-app" />;
  }

  // Check email verification FIRST (before profile setup)
  if (needsEmailVerification) {
    return <VerifyEmail />;
  }

  // Check if profile needs to be completed
  if (needsProfile) {
    return <Onboarding />;
  }

  // Check if device needs confirmation before force sign-in
  if (!privileged && deviceLock?.needsConfirmation) {
    return <ForceSignInWarning />;
  }

  // Check if device is locked due to cooldown
  if (!privileged && deviceLock?.locked) {
    return <DeviceLock />;
  }

  return (
    <>
      <Layout />
      <NewsModal visible={newsModalVisible} news={newsItems} onClose={handleCloseNews} />
    </>
  );
}
